import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useEvent } from 'expo';
import { useVideoPlayer, VideoView } from 'expo-video';
import { Platform, StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { type SharedValue, useAnimatedStyle } from 'react-native-reanimated';

import {
  addHttpsProtocolToURL,
  ContentfulImage,
  ContentfulImageTransformOptions,
  FadeView,
  LoadingDots,
  theme,
  useRemoteAsset,
} from '@sg/garnish';

import { type ProductDetailsCardCarouselItem } from '../../ProductDetailsCarousel.types';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsCarouselSlide = memo((props: Props) => {
  const { item, isActive, widthSV, heightSV } = props;

  const containerRef = useRef<View>(null);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerAnimatedStyles = useAnimatedStyle(() => {
    return {
      width: widthSV.value,
      height: heightSV.value,
    };
  });

  const containerStyles = [styles.slide, containerAnimatedStyles];

  // ─── Image Variation ─────────────────────────────────────────────────

  if (item.type === 'image') {
    return (
      <Animated.View
        ref={containerRef}
        style={containerStyles}
        aria-selected={isActive}
        pointerEvents="none"
      >
        <ContentfulImage
          baseUrl={item.source}
          autoplay={Platform.OS === 'ios' ? isActive : undefined} // NOTE: Autoplay is not stable on Android
          staticBaseUrl={item.staticSource}
          options={IMAGE_TRANSFORM_CONFIG_CONTENTFUL}
          alt={item.alt}
          style={styles.media}
          contentFit={item.contentFit ?? 'cover'}
          withLoadingSkeleton
        />
      </Animated.View>
    );
  }

  // ─── Video Variation ─────────────────────────────────────────────────

  return (
    <Animated.View
      ref={containerRef}
      style={containerStyles}
      aria-selected={isActive}
      pointerEvents="none"
    >
      <ProductDetailsCardCarouselSlideVideo
        url={item.source}
        isActive={isActive}
        staticImageUrl={item.staticSource}
      />
    </Animated.View>
  );
});

// ─────────────────────────────────────────────────────────────────────────────

const ProductDetailsCardCarouselSlideVideo = memo((props: VideoProps) => {
  const { url, isActive, staticImageUrl } = props;

  // ─────────────────────────────────────────────────────────────────────

  const formattedURL = addHttpsProtocolToURL(url);
  const { localURI: cachedVideoURI = null } = useRemoteAsset({ url: formattedURL }); // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  if (!cachedVideoURI) {
    return (
      <View style={styles.mediaContainer}>
        <LoadingDots color={theme.colors.DARK_KALE} />
      </View>
    );
  }

  return (
    <View
      style={styles.mediaContainer}
      {...(staticImageUrl ? { dataSet: { staticSource: staticImageUrl } } : {})}
    >
      <ProductDetailsCardCarouselSlideVideoReady url={cachedVideoURI} isActive={isActive} />
    </View>
  );
});

const ProductDetailsCardCarouselSlideVideoReady = (props: VideoReadyProps) => {
  const { url, isActive } = props;

  const player = useVideoPlayer(url, (player) => {
    player.loop = true;
    player.muted = true;
  });

  const { status } = useEvent(player, 'statusChange', { status: player.status }); // prettier-ignore

  const isLoading = status !== 'readyToPlay';

  // ─── Refs ────────────────────────────────────────────────────────────

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const togglePlay = useCallback(() => {
    if (!isActive) {
      player.pause();

      return;
    }

    player.replay();
    player.play(); // NOTE: For some reason, the video doesn't play on iOS without this
  }, [isActive, player]);

  /**
   * Disables looping the video after timeout to prevent having long running videos
   * to save data and battery.
   */
  const pauseAfterTimeout = useCallback(() => {
    if (!isActive) return;

    timeoutRef.current = setTimeout(() => {
      player.loop = false;
    }, 60 * 1000); // 1 minute

    // Cleanup
    return () => {
      player.loop = true;

      if (!timeoutRef.current) return;

      clearTimeout(timeoutRef.current);
    };
  }, [isActive, player]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(togglePlay, [togglePlay]);
  useEffect(pauseAfterTimeout, [pauseAfterTimeout]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.mediaContainer}>
      <VideoView player={player} contentFit="cover" style={styles.media} nativeControls={false} />

      <FadeView show={isLoading} style={StyleSheet.absoluteFill}>
        <LoadingDots color={theme.colors.DARK_KALE} />
      </FadeView>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_TRANSFORM_CONFIG_CONTENTFUL: Partial<ContentfulImageTransformOptions> = {
  fm: 'webp',
  q: 70,
  w: 1000,
  fit: 'fill',
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  slide: {
    width: '100%',
    height: '100%',
  },
  mediaContainer: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  media: {
    width: '100%',
    height: '100%',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  item: ProductDetailsCardCarouselItem;
  isActive: boolean;
  widthSV: SharedValue<number>;
  heightSV: SharedValue<number>;
};

type VideoProps = {
  url: string;
  isActive: boolean;
  staticImageUrl?: string;
};

type VideoReadyProps = {
  url: string;
  isActive: boolean;
};
